import React, { useEffect, useState } from 'react';
import {
    Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Button, Dialog,
    DialogActions, DialogContent, DialogTitle,
    TextField, IconButton,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Snackbar,
    Alert,
    CircularProgress,
    Backdrop,
    Tooltip,
    Chip,
    InputAdornment,
} from '@mui/material';
import { Edit, Delete, LocalGasStation, Add, Save, CalendarMonth, Label, Payments, Co2, DirectionsCar } from '@mui/icons-material';
import axios from 'axios';
import { create_fuels, delete_fuels, edit_fuels, fuelsed } from "../api/API";
import { AdminPage, GREEN, LIGHT, BORDER, primaryButtonSx } from '../admin/AdminPageLayout';

const headCellSx = { bgcolor: LIGHT, color: GREEN, fontWeight: 700, textAlign: 'center', whiteSpace: 'nowrap', borderBottom: `2px solid ${BORDER}` };
const cellSx = { textAlign: 'center' };

function FuelTable() {
    const [fuels, setFuels] = useState([]);
    const [open, setOpen] = useState(false);
    const [editFuel, setEditFuel] = useState(null);
    const [formData, setFormData] = useState({ type_car: '', name: '', price: 0.0, co2_per_l: 0.0 });
    const token = localStorage.getItem("token");
    const [loading, setLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // หรือ "error"

    const fetchFuels = async () => {
        const res = await axios.get(fuelsed,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });
        console.log("res.data", res.data);
        setFuels(res.data);
    };

    useEffect(() => {
        fetchFuels();
    }, []);

    const handleOpen = (fuel = null) => {
        setEditFuel(fuel);
        if (fuel) {
            setFormData(fuel);
        } else {
            setFormData({ type_car: '', name: '', price: 0.0, co2_per_l: 0.0 });
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setEditFuel(null);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            if (editFuel) {
                await axios.put(`${edit_fuels}/${editFuel.ID}`, formData, {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                });
                setSnackbarMessage("✅ แก้ไขข้อมูลสำเร็จ");
            } else {
                await axios.post(create_fuels, formData, {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                });
                setSnackbarMessage("✅ เพิ่มข้อมูลสำเร็จ");
            }

            setSnackbarSeverity("success");
            setSnackbarOpen(true);
            await fetchFuels();
            handleClose();
        } catch (err) {
            console.error('Error saving fuel:', err);
            setSnackbarMessage("❌ เกิดข้อผิดพลาดในการบันทึกข้อมูล");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('คุณแน่ใจหรือไม่ว่าต้องการลบรายการนี้?')) {
            await axios.delete(`${delete_fuels}/${id}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            fetchFuels();
        }
    };

    return (
        <>
            <AdminPage
                icon={<LocalGasStation fontSize="large" />}
                title="ตารางราคาน้ำมัน"
                subtitle="จัดการประเภทน้ำมัน ราคา และค่าการปล่อย CO2"
                maxWidth={1100}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: 2,
                        mb: 3,
                    }}
                >
                    <Chip
                        icon={<CalendarMonth />}
                        label="ราคาน้ำมันวันที่ 19 ตุลาคม 2568"
                        sx={{ bgcolor: LIGHT, color: GREEN, fontWeight: 600, border: `1px solid ${BORDER}`, alignSelf: { xs: 'flex-start', sm: 'center' } }}
                    />
                    <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()} sx={primaryButtonSx}>
                        เพิ่มน้ำมัน
                    </Button>
                </Box>

                <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{ borderRadius: 3, borderColor: BORDER, overflowX: 'auto' }}
                >
                    <Table sx={{ minWidth: 700 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={headCellSx}>ลำดับ</TableCell>
                                <TableCell sx={headCellSx}>ปรเภทรถ</TableCell>
                                <TableCell sx={headCellSx}>ชื่อ</TableCell>
                                <TableCell sx={headCellSx}>ราคา (บาท/ลิตร)</TableCell>
                                <TableCell sx={headCellSx}>การกินน้ำมัน (kg/ลิตร)</TableCell>
                                <TableCell sx={headCellSx}>แก้ไข</TableCell>
                                <TableCell sx={headCellSx}>ลบ</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {fuels?.map((fuel, index) => (
                                <TableRow
                                    key={fuel.id}
                                    hover
                                    sx={{ '&:last-child td': { borderBottom: 0 } }}
                                >
                                    <TableCell sx={cellSx}>
                                        {index + 1}
                                    </TableCell>
                                    <TableCell sx={cellSx}>{
                                        fuel.type_car === "motorcy" ? "มอเตอร์ไซต์"
                                            : fuel.type_car === "car" ? "รถยนต์"
                                                : fuel.type_car === "van" && "รถตู้"
                                    }</TableCell>
                                    <TableCell sx={{ ...cellSx, fontWeight: 600 }}>{fuel.name}</TableCell>
                                    <TableCell sx={cellSx}>{fuel.price}</TableCell>
                                    <TableCell sx={cellSx}>{fuel.co2_per_l}</TableCell>
                                    <TableCell sx={cellSx}>
                                        <Tooltip title="แก้ไข">
                                            <IconButton color="primary" onClick={() => handleOpen(fuel)}>
                                                <Edit />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell sx={cellSx}>
                                        <Tooltip title="ลบ">
                                            <IconButton color="error" onClick={() => handleDelete(fuel.ID)}>
                                                <Delete />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </AdminPage>

            {/* Dialog สำหรับเพิ่ม/แก้ไข */}
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 4 } }}>
                <DialogTitle
                    sx={{
                        color: 'white',
                        fontWeight: 'bold',
                        background: `linear-gradient(135deg, ${GREEN} 0%, #43a047 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <LocalGasStation />
                    {editFuel ? 'แก้ไขข้อมูลน้ำมัน' : 'เพิ่มข้อมูลน้ำมัน'}
                </DialogTitle>
                <DialogContent>
                    {/* ตัวอย่าง Select เพิ่มเติม (เลือกประเภทรถ) */}
                    <Box
                        sx={{ mt: 3 }}
                    >
                        <FormControl fullWidth>
                            <InputLabel
                                id="type-car-label"
                            >
                                ประเภทรถ
                            </InputLabel>
                            <Select
                                labelId="type-car-label"
                                id="type-car"
                                value={formData.type_car}
                                label="ประเภทรถ"
                                onChange={(e) => setFormData({ ...formData, type_car: e.target.value })}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <DirectionsCar sx={{ color: GREEN }} />
                                    </InputAdornment>
                                }
                            >

                                <MenuItem value={"motorcy"}>มอเตอร์ไซค์</MenuItem>
                                <MenuItem value={"car"}>รถยนต์</MenuItem>
                                <MenuItem value={"van"}>รถตู้</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="ชื่อ"
                            name="name"
                            fullWidth
                            margin="normal"
                            value={formData.name}
                            onChange={handleChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Label sx={{ color: GREEN }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            label="ราคา (บาท/ลิตร)"
                            name="price"
                            type="number"
                            fullWidth
                            margin="normal"
                            value={formData.price}
                            onChange={(e) =>
                                setFormData({ ...formData, price: parseFloat(e.target.value) })
                            }
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Payments sx={{ color: GREEN }} />
                                    </InputAdornment>
                                ),
                                endAdornment: <InputAdornment position="end">บาท/ลิตร</InputAdornment>,
                            }}
                        />
                        <TextField
                            label="CO2 (kg/ลิตร)"
                            name="co2_per_l"
                            type="number"
                            fullWidth
                            margin="normal"
                            value={formData.co2_per_l}
                            onChange={(e) =>
                                setFormData({ ...formData, co2_per_l: parseFloat(e.target.value) })
                            }
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Co2 sx={{ color: GREEN }} />
                                    </InputAdornment>
                                ),
                                endAdornment: <InputAdornment position="end">kg/ลิตร</InputAdornment>,
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, bgcolor: '#fafafa', borderTop: '1px solid #eee', gap: 1, flexWrap: 'wrap' }}>
                    <Button onClick={handleClose} sx={{ borderRadius: 3, color: 'text.secondary' }}>ยกเลิก</Button>
                    <Button onClick={handleSubmit} variant="contained" size="large" startIcon={<Save />} sx={primaryButtonSx}>
                        บันทึก
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} variant="filled" sx={{ width: '100%', borderRadius: 3 }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>

            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={loading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </>
    )
}

export default FuelTable